import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TitleProvider } from './shared/hooks/TitleContext';
import { FaHeadset, FaPhone, FaTimes, FaDownload } from 'react-icons/fa';
import { useTheme } from './shared/contexts/ThemeContext';
import { isLandingHost } from './utils/tenant';
import { isAdminPath } from './utils/adminPath';
import { PosProvider } from './features/dashboard/pages/POS-Departments/context/PosContext';
import './App.css';
import './shared/styles/cyber-pages-light.css';
import './shared/styles/responsive.css';
import { ToastContainer } from 'react-toastify';
import { offlineDetector } from './utils/offlineDetector';
import { useDispatch } from 'react-redux';
import { quickPWACheck } from './utils/pwaChecker';
import 'react-toastify/dist/ReactToastify.css';
//10/11/2025 تاريخ استلام المشروع احمد حلمي

// Code-split: LandingApp + ErpApp في chunks منفصلة عشان apex visit ما يحمّلش
// كل ERP tree (200+ component)، والعكس صحيح. اختيار الـ chunk بيحصل من
// `isLandingHost()` اللي بيقرأ الـ subdomain من الـ window.location.hostname.
const LandingApp = lazy(() => import('./LandingApp'));
const ErpApp = lazy(() => import('./ErpApp'));
// Super-admin panel: fully separate chunk, own Router/CSS (Tailwind, no
// Bootstrap) — see isAdminPath() below for why it's checked ahead of the
// landing/tenant split.
const AdminApp = lazy(() => import('./AdminApp'));

// PWA install prompt timing — small icon by default, banner re-opens periodically.
// Defined at module scope so they are referentially stable for useEffect deps.
const PWA_INITIAL_DELAY_MS = 5000;        // wait 5s after beforeinstallprompt before the first banner
const PWA_BANNER_AUTO_CLOSE_MS = 8000;    // each time the banner auto-opens, it stays open 8s then collapses to the icon
const PWA_PERIODIC_INTERVAL_MS = 5 * 60 * 1000; // every 5 minutes, re-open the banner as a reminder

// Minimal Suspense fallback — مش بنعرض branding أو spinner تقيل عشان أول paint
// يفضل خفيف. الـ chunk عادة بيوصل في < 200ms على connections عادية.
const RouteFallback = () => (
  <div className="route-fallback" aria-hidden="true" />
);

function App() {
  // Super-admin panel is a fully separate SPA (own Router, own Tailwind CSS,
  // no Bootstrap) — it must not mount any of the ERP/landing chrome below
  // (PWA install prompt, support widget, PosProvider, react-toastify theme
  // tied to the ERP ThemeContext, etc), so it's returned before any of that
  // renders or its hooks run.
  if (isAdminPath()) {
    return (
      <Suspense fallback={<div aria-hidden="true" />}>
        <AdminApp />
      </Suspense>
    );
  }

  return <AppShell />;
}

function AppShell() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isLight } = useTheme();
  const [showSupportNumbers, setShowSupportNumbers] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  // Whether the install prompt is collapsed to a small floating icon (true)
  // or expanded to the full banner (false). Default collapsed so the user
  // sees a subtle install icon that can be expanded on demand.
  const [installCollapsed, setInstallCollapsed] = useState(true);
  const isArabic = i18n.language === 'ar';
  const onLandingHost = isLandingHost();

  // أرقام التواصل للدعم الفني
  const supportContacts = [
    {
      label: isArabic ? "الدعم الفني" : "Technical Support",
      phone: "+201234567890",
      icon: <FaHeadset />
    },
    {
      label: isArabic ? "الدعم التقني" : "Technical Assistance",
      phone: "+201234567891",
      icon: <FaPhone />
    },
    {
      label: isArabic ? "الاستفسارات" : "Inquiries",
      phone: "+201234567892",
      icon: <FaPhone />
    }
  ];

  // Set dispatch for offline detector to process queued requests
  useEffect(() => {
    offlineDetector.setDispatch(dispatch);
  }, [dispatch]);

  // Quick PWA status check on app load
  useEffect(() => {
    quickPWACheck();
  }, []);

  // PWA Install Prompt Handler - Now handled by PWAInstallContext
  // This useEffect is kept for backward compatibility with the floating prompt
  useEffect(() => {
    // Check if app is already installed
    const checkInstalled = () => {
      return window.matchMedia('(display-mode: standalone)').matches ||
             window.navigator.standalone === true ||
             document.referrer.includes('android-app://');
    };

    const isInstalled = checkInstalled();

    // Only skip if app is CURRENTLY installed (running in standalone mode)
    if (isInstalled) {
      return; // App is already installed and running
    }

    // Listen for beforeinstallprompt event
    // This will fire even if user previously dismissed it (after page reload)
    let firstBannerCollapseTimer;
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install prompt after the initial delay; the banner expands
      // briefly so the user notices, then auto-collapses to a small icon.
      setTimeout(() => {
        setShowInstallPrompt(true);
        setInstallCollapsed(false);
        firstBannerCollapseTimer = setTimeout(() => {
          setInstallCollapsed(true);
        }, PWA_BANNER_AUTO_CLOSE_MS);
      }, PWA_INITIAL_DELAY_MS);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Also check periodically if prompt becomes available
    // Sometimes browsers need a page reload to re-trigger the event
    const checkInterval = setInterval(() => {
      if (!checkInstalled() && !deferredPrompt) {
        // Prompt might become available again
      }
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearInterval(checkInterval);
      if (firstBannerCollapseTimer) clearTimeout(firstBannerCollapseTimer);
    };
  }, [deferredPrompt]);

  // Periodic reminder: while the install icon is visible, every few minutes
  // re-open the banner for a few seconds so the user is reminded that the
  // app can be installed. After auto-close it shrinks back to the icon.
  useEffect(() => {
    if (!showInstallPrompt) return;
    const interval = setInterval(() => {
      setInstallCollapsed(false);
      setTimeout(() => setInstallCollapsed(true), PWA_BANNER_AUTO_CLOSE_MS);
    }, PWA_PERIODIC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [showInstallPrompt]);

  // Handle PWA install
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // If no deferred prompt, try to show browser's native install prompt
      // This works for browsers that support it
      if (window.matchMedia('(display-mode: standalone)').matches) {
        // Already installed
        return;
      }
      // Show instructions for manual install
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);

      if (isIOS) {
        alert(i18n.language === 'ar'
          ? 'للتثبيت: اضغط على زر المشاركة ثم "إضافة إلى الشاشة الرئيسية"'
          : 'To install: Tap the share button then "Add to Home Screen"'
        );
      } else if (isAndroid) {
        alert(i18n.language === 'ar'
          ? 'للتثبيت: اضغط على القائمة (⋮) ثم "تثبيت التطبيق"'
          : 'To install: Tap the menu (⋮) then "Install app"'
        );
      } else {
        alert(i18n.language === 'ar'
          ? 'للتثبيت: اضغط على أيقونة التثبيت في شريط العنوان'
          : 'To install: Click the install icon in the address bar'
        );
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  // Monitor online/offline status
  // useEffect(() => {
  //   let hasShownInitialStatus = false;

  //   const handleStatusChange = (isOnline) => {
  //     // Don't show toast on initial load
  //     if (!hasShownInitialStatus) {
  //       hasShownInitialStatus = true;
  //       return;
  //     }

  //     if (isOnline) {
  //       toast.success('Connection restored', {
  //         position: 'top-right',
  //         autoClose: 3000,
  //       });
  //     } else {
  //       toast.warning('No internet connection - App is working offline', {
  //         position: 'top-right',
  //         autoClose: 5000,
  //       });
  //     }
  //   };

  //   // Get initial status without showing toast
  //   const initialStatus = offlineDetector.getStatus();
  //   hasShownInitialStatus = true;

  //   offlineDetector.onStatusChange(handleStatusChange);

  //   return () => {
  //     offlineDetector.removeListener(handleStatusChange);
  //   };
  // }, []);

  useEffect(() => {
    const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  return (<>
    <TitleProvider>
      <PosProvider>
      <Router>
        {/* Global Support Technical Icon - Bottom Left with Toggle Div */}
        <div className="support-container">
          <button
            className={`support-button ${showSupportNumbers ? 'active hidden' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowSupportNumbers(!showSupportNumbers);
            }}
            title={isArabic ? "الدعم الفني" : "Technical Support"}
            type="button"
          >
            <FaHeadset />
          </button>

          {/* Support Numbers Toggle Div */}
          {showSupportNumbers && (
            <div className="support-numbers-div">
              <div className="support-numbers-header">
                <h4>
                  <FaHeadset className={`support-header-icon ${isArabic ? 'rtl' : 'ltr'}`} />
                  {isArabic ? "الدعم الفني والتقني" : "Technical Support"}
                </h4>
                <button
                  className="support-close-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowSupportNumbers(false);
                  }}
                  type="button"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="support-numbers-body">
                {supportContacts.map((contact, index) => (
                  <div key={index} className="support-contact-item">
                    <div className="support-contact-icon">
                      {contact.icon}
                    </div>
                    <div className="support-contact-info">
                      <span className="support-contact-label">{contact.label}</span>
                      <a
                        href={`tel:${contact.phone}`}
                        className="support-contact-phone"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {contact.phone}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* PWA Install Prompt — small floating install icon (collapsed) that
            expands into the original full banner on click. Auto-collapses
            after a few seconds, and re-opens periodically as a gentle
            reminder. Dismissing with × just collapses to the icon; the
            periodic reminder will keep showing the banner. */}
        {showInstallPrompt && deferredPrompt && installCollapsed && (
          <button
            type="button"
            onClick={() => setInstallCollapsed(false)}
            title={i18n.language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
            aria-label={i18n.language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
            className={`pwa-install-fab ${isLight ? 'light' : 'dark'} ${isArabic ? 'rtl' : 'ltr'}`}
          >
            <FaDownload />
          </button>
        )}

        {showInstallPrompt && deferredPrompt && !installCollapsed && (
          <div className={`pwa-install-banner ${isLight ? 'light' : 'dark'} ${isArabic ? 'rtl' : 'ltr'}`}>
            <div className={`pwa-install-banner-icon ${isLight ? 'light' : 'dark'}`}>
              <FaDownload />
            </div>
            <div className="pwa-install-banner-content">
              <div className={`pwa-install-banner-title ${isLight ? 'light' : 'dark'}`}>
                {i18n.language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
              </div>
              <div className={`pwa-install-banner-text ${isLight ? 'light' : 'dark'}`}>
                {i18n.language === 'ar'
                  ? 'ثبت التطبيق للوصول السريع'
                  : 'Install for quick access'}
              </div>
            </div>
            <button
              onClick={handleInstallClick}
              className={`pwa-install-banner-action ${isLight ? 'light' : 'dark'}`}
            >
              {i18n.language === 'ar' ? 'تثبيت' : 'Install'}
            </button>
            <button
              type="button"
              onClick={() => {
                // Collapse back to the icon. The icon stays visible for manual
                // access and the periodic reminder will re-open the banner later.
                setInstallCollapsed(true);
              }}
              className={`pwa-install-banner-close ${isLight ? 'light' : 'dark'}`}
            >
              ×
            </button>
          </div>
        )}

        <div className="App" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          {/* اللاندينج والـ ERP في chunks منفصلة — Suspense بيظهر fallback خفيف
              لحد ما الـ chunk المطلوب يوصل. الـ tenant detection بيختار الواحد. */}
          <Suspense fallback={<RouteFallback />}>
            {onLandingHost ? <LandingApp /> : <ErpApp />}
          </Suspense>
        </div>

      </Router>
      </PosProvider>
    </TitleProvider>

    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={i18n.language === 'ar'}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={isLight ? 'light' : 'dark'}
    />
  </>
  );
}

export default App;
